import React from 'react';
import type { GameType } from '@aco/shared';
import { PlayerSelector } from './PlayerSelector.js';
import type { PlayerResult } from '../services/api.js';

interface PlayerSetupCardProps {
  isAuthenticated: boolean;
  isDoubles: boolean;
  setIsDoubles: (val: boolean) => void;
  gameType: GameType;
  setGameType: (val: GameType) => void;
  p1a: string; setP1a: (v: string) => void;
  p1b: string; setP1b: (v: string) => void;
  p2a: string; setP2a: (v: string) => void;
  p2b: string; setP2b: (v: string) => void;
  hasDuplicatePlayers: boolean;
  canStart: boolean;
  onStart: () => void;
  recentPlayers: PlayerResult[];
  primaryColor: string;
  textColor: string;
  skinsEnabled: boolean;
}

const labelStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  opacity: 0.7,
  marginBottom: '6px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 16px',
  fontSize: '1.1rem',
  border: '2px solid rgba(255,255,255,0.15)',
  borderRadius: '10px',
  background: 'rgba(255,255,255,0.08)',
  outline: 'none',
  boxSizing: 'border-box',
};

/**
 * Player setup card — Team 1/2 name fields, format/game type toggles,
 * duplicate warning, and start button.
 */
export function PlayerSetupCard({
  isAuthenticated,
  isDoubles,
  setIsDoubles,
  gameType,
  setGameType,
  p1a, setP1a,
  p1b, setP1b,
  p2a, setP2a,
  p2b, setP2b,
  hasDuplicatePlayers,
  canStart,
  onStart,
  recentPlayers,
  primaryColor,
  textColor,
  skinsEnabled,
}: PlayerSetupCardProps) {
  const inputStyle: React.CSSProperties = { ...inputBaseStyle, color: textColor };

  const toggleBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '14px',
    fontSize: '1rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '10px',
    background: active ? primaryColor : 'rgba(255,255,255,0.08)',
    color: textColor,
    cursor: 'pointer',
    touchAction: 'manipulation',
  });

  const renderPlayerField = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
    ariaLabel: string,
    excludeNames: string[],
    marginTop?: string,
  ) => {
    if (isAuthenticated) {
      return (
        <PlayerSelector
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          recentPlayers={recentPlayers}
          primaryColor={primaryColor}
          textColor={textColor}
          aria-label={ariaLabel}
          excludeNames={excludeNames}
          inputStyle={marginTop ? { marginTop } : undefined}
        />
      );
    }
    return (
      <input
        style={{ ...inputStyle, ...(marginTop ? { marginTop } : {}) }}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    );
  };

  return (
    <div
      style={{
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderRadius: '16px',
        padding: '28px',
        width: 'min(100%, 600px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* Player names — Team 1 */}
      <div>
        <div style={labelStyle} role="group" aria-label="Team 1 players">
          Team 1
        </div>
        {renderPlayerField(
          p1a, setP1a,
          isDoubles ? 'Player 1A name' : 'Player 1 name',
          'Team 1 player A name',
          [p1b, p2a, p2b].filter(Boolean),
        )}
        {isDoubles &&
          renderPlayerField(
            p1b, setP1b,
            'Player 1B name',
            'Team 1 player B name',
            [p1a, p2a, p2b].filter(Boolean),
            '10px',
          )}
      </div>

      {/* Player names — Team 2 */}
      <div>
        <div style={labelStyle} role="group" aria-label="Team 2 players">
          Team 2
        </div>
        {renderPlayerField(
          p2a, setP2a,
          isDoubles ? 'Player 2A name' : 'Player 2 name',
          'Team 2 player A name',
          [p1a, p1b, p2b].filter(Boolean),
        )}
        {isDoubles &&
          renderPlayerField(
            p2b, setP2b,
            'Player 2B name',
            'Team 2 player B name',
            [p1a, p1b, p2a].filter(Boolean),
            '10px',
          )}
      </div>

      {/* Match format */}
      <div>
        <div style={labelStyle}>Match Format</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={toggleBtnStyle(!isDoubles)} onClick={() => setIsDoubles(false)}>
            Singles
          </button>
          <button style={toggleBtnStyle(isDoubles)} onClick={() => setIsDoubles(true)}>
            Doubles
          </button>
        </div>
      </div>

      {/* Game type */}
      <div>
        <div style={labelStyle}>Game Type</div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            style={toggleBtnStyle(gameType === 'standard21')}
            onClick={() => setGameType('standard21')}
          >
            Standard 21
          </button>
          {skinsEnabled && isAuthenticated && (
            <button
              style={toggleBtnStyle(gameType === 'skins')}
              onClick={() => setGameType('skins')}
            >
              Skins
            </button>
          )}
        </div>
      </div>

      {/* Duplicate player warning */}
      {hasDuplicatePlayers && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            background: 'rgba(255,80,80,0.15)',
            border: '1.5px solid rgba(255,80,80,0.4)',
            color: '#ff8080',
            fontSize: '0.9rem',
            fontWeight: 600,
          }}
        >
          ⚠️ The same player can't be on both teams. Please choose different players.
        </div>
      )}

      {/* Start button */}
      <button
        onClick={onStart}
        disabled={!canStart}
        style={{
          padding: '18px',
          fontSize: '1.3rem',
          fontWeight: 800,
          border: 'none',
          borderRadius: '12px',
          background: canStart ? primaryColor : 'rgba(255,255,255,0.1)',
          color: '#ffffff',
          cursor: canStart ? 'pointer' : 'not-allowed',
          touchAction: 'manipulation',
          transition: 'background 0.2s ease',
        }}
        aria-label="Start game"
      >
        Start Game
      </button>
    </div>
  );
}
